import * as React from 'react';
import { useQuery, useReactiveVar, useApolloClient } from '@apollo/client';
import { useTheme } from '@mui/material';
import { useFormik } from 'formik';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import * as Device from 'react-device-detect';
import moment from 'moment';

import { loginStatusVar } from '@context';
import { useConnection, useRecaptcha } from '@hooks';
import { GET_LOGIN_META } from '@graphql';
import { useValidation } from './validationSchema';
import useStyles from './styles';
import initValues from './initValues';

interface LoginFormProps {
  redirect?: string | null;
}

export default function LoginForm(props: Readonly<LoginFormProps>) {
  const { redirect = null } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const loginStatus = useReactiveVar(loginStatusVar);
  const [showPassword, setShowPassword] = React.useState(false);
  const [alert, setAlert] = React.useState<string | null>(null);
  const [unlockTime, setUnlockTime] = React.useState<number | null>(null);
  const { t } = useTranslation('authentication');
  const { login } = useConnection({ redirect });
  const client = useApolloClient();
  const { executeRecaptcha, isRecaptchaReady } = useRecaptcha();

  const formik = useFormik({
    initialValues: initValues(),
    validationSchema: useValidation(),
    async onSubmit(input) {
      if (!isRecaptchaReady) {
        // reCAPTCHA script not ready yet
        return;
      }

      const recaptchaToken = await executeRecaptcha('login');
      Object.assign(input, { recaptchaToken });

      login({ variables: { input } })
        .then(() => {
          formik.resetForm();
        })
        .catch((e) => {
          const message = e.message || '';
          const isLocked = e.extra?.isLocked ?? false;
          const unlockedAt = e.extra?.unlockedAt ?? null;

          if (isLocked) {
            setUnlockTime(unlockedAt);
            // Update the Apollo Client cache with the unlock time
            client.writeQuery({
              query: GET_LOGIN_META,
              variables: { email: input.email },
              data: {
                loginMeta: {
                  __typename: 'LoginMeta',
                  email: input.email,
                  unlockTime: unlockedAt,
                },
              },
            });
            setAlert(
              t('alert.Too many attempts, please wait before trying again', {
                time: moment(unlockedAt).fromNow(),
              })
            );
          } else if (message === 'INVALID_CREDENTIALS') {
            setAlert(t('alert.Wrong credentials'));
          }
        });
    },
  });

  // Fetch the login meta data to get the unlock time
  const { data: cachedLoginMeta } = useQuery(GET_LOGIN_META, {
    variables: { email: formik.values.email },
    skip: !!formik.errors.email || !formik.values.email.length,
  });

  // Update unlock time and alert when cachedLoginMeta changes
  React.useEffect(() => {
    if (!cachedLoginMeta?.loginMeta) {
      !!unlockTime && setUnlockTime(null);
      !!alert && setAlert(null);
      return;
    }

    const { unlockTime: cachedUnlockTime } = cachedLoginMeta.loginMeta;

    if (cachedUnlockTime && moment(cachedUnlockTime).isAfter(moment())) {
      setUnlockTime(cachedUnlockTime);
      setAlert(
        t('alert.Too many attempts, please wait before trying again', {
          time: moment(cachedUnlockTime).fromNow(),
        })
      );
      // clear password field
      formik.setFieldValue('password', '');
      formik.setFieldTouched('password', false);
    } else {
      // Update the Apollo Client cache removing the unlock time
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'loginMeta',
        args: { email: formik.values.email },
      });
      client.cache.gc();
    }
  }, [cachedLoginMeta, formik.values]);

  return (
    <form style={styles.root} noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
      <Stack spacing={Device.isMobile ? 2 : 3}>
        <TextField
          required
          fullWidth
          size="small"
          type="email"
          name="email"
          autoComplete="email"
          label={t('field.email.label')}
          placeholder="ex: contact@festayre.com"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          disabled={loginStatus.isLogging}
        />
        <TextField
          required
          fullWidth
          name="password"
          size="small"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          label={t('field.password.label')}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          disabled={loginStatus.isLogging || !!unlockTime}
          slotProps={{
            input: {
              endAdornment: showPassword ? (
                <theme.icons.see
                  fontSize="small"
                  sx={styles.visibilityIcon}
                  onClick={() => setShowPassword(!showPassword)}
                />
              ) : (
                <theme.icons.hide
                  fontSize="small"
                  sx={styles.visibilityIcon}
                  onClick={() => setShowPassword(!showPassword)}
                />
              ),
            },
          }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Link
            href="#"
            color="textPrimary"
            underline="hover"
            variant="body2"
            fontSize={{ xs: '.7rem', sm: '.8rem' }}
          >
            {t('forgot password?')}
          </Link>
        </Stack>
        {alert && (
          <Alert severity={!!unlockTime ? 'error' : 'warning'} icon={!!unlockTime ? undefined : false}>
            {alert}
          </Alert>
        )}
        <Button
          disabled={!isRecaptchaReady || !formik.dirty || !formik.isValid}
          loading={loginStatus.isLogging}
          variant="contained"
          fullWidth
          size="small"
          color="primary"
          type="submit"
          sx={{
            '&.Mui-disabled': {
              background: alpha(theme.palette.background.default, 0.5),
              color: alpha(theme.palette.text.secondary, 0.5),
            },
          }}
        >
          {t('button.Connection')}
        </Button>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" align="center">
          {t('recaptcha.This site is protected by reCAPTCHA and the Google')}{' '}
          <Link href="https://policies.google.com/privacy" target="_blank" color="text.secondary">
            {t('recaptcha.Privacy Policy')}
          </Link>{' '}
          &{' '}
          <Link href="https://policies.google.com/terms" target="_blank" color="text.secondary">
            {t('recaptcha.Terms of Service')}
          </Link>{' '}
          {t('recaptcha.apply.')}
        </Typography>
      </Stack>
    </form>
  );
}
